import useSWR from "swr";
import {Utils} from "../common/utils";
import {orderCategories} from "Components/Dashboard/list";
import * as _ from "lodash";
const clientConfigs = Utils.getEAIClientConfigs();

export interface Dashboard {
    id: number
    workbook_id: string
    url: string
    name: string
    description: string
    thumbnail_url_300: string
    thumbnail_url_600: string
    short_name: string
    datasource?: { updated: string, time_span: string }
    published_date?: string
    updated?: string
    status?: string
    project?: string
    workbook_name?: string
    category?: string
    faculty_can_view?: boolean
    mentors_can_view?: boolean
    public_can_view?: boolean
    admins_can_view?: boolean
    datasource_name?: string
    custom_order?: number
    dashboard_type: string
    byo_dashboard_type?: string
    use_url?: boolean
    embedded_code?: string
    is_responsive?: boolean
    decoded?: boolean
}

export interface DashboardCategoryDict {
    [p: string]: Dashboard[]
}

export interface DashboardsResponse{
    data: Record<string, Dashboard[]>
}

function decodeDashboardsInCategories(newDashboards: DashboardCategoryDict) {
    for (const key in newDashboards) {
        // decode embedded code for BYODashboard and use_url false
        newDashboards[key] = newDashboards[key].map(dashboard => {
            if (dashboard.use_url === false && dashboard.embedded_code && dashboard.dashboard_type === "BYODashboard" && !dashboard.decoded) {
                dashboard.embedded_code = Utils.decodeBase64(dashboard.embedded_code);
                dashboard.decoded = true;
            }
            return dashboard;
        });
    }
    return newDashboards;
}


export function useDashboardsInCategories(
    getAllDashboards = false,
    excludePPC = false,
    grouped = true,
    excluded_type: string | null = null,
    orderBy: orderCategories = "group",
    reloadCache = false,
    favorites = true
) {
    const desc = orderBy === "name desc";
    const order = orderBy.includes("name") ? "name" : orderBy;
    const params: Record<string, string> = {
        get_all_status: getAllDashboards ? "true" : "false",
        exclude_ppc: excludePPC ? "true" : "false",
        grouped: grouped ? "true" : "false",
        exclude_type: excluded_type ? excluded_type : "",
        order_by: order === "group" ? "" : order,
        desc: desc ? "true" : "false",
        reload_cache: reloadCache ? "true" : "false",
        favorites: favorites ? "true" : "false"
    };
    const re = Utils.generateQueryParams(params);

    const {data, error, mutate, isLoading} = useSWR<DashboardsResponse, Error>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/dashboards/urls?${re}`);
    if (!data) {
        return {
            data: null,
            dataFavorites: null,
            dataFlat: [] as Dashboard[],
            byoDashboards: [] as Dashboard[],
            error,
            isLoading,
            mutate
        };
    }
    const dataFavorites: Dashboard[] = [];

    const dashboardsInCategories = decodeDashboardsInCategories(data.data);

    if (dashboardsInCategories["FAVORITE DASHBOARDS"]) {
        // push to favorites
        dataFavorites.push(...data.data["FAVORITE DASHBOARDS"]);
    }
    const dataFlat: Dashboard[] = _.flatMap(dashboardsInCategories, (dashArr: Dashboard[]) => dashArr.slice());
    const byoDashboards = dataFlat.filter(dashboard => dashboard.dashboard_type === "BYODashboard");
    return {
        data: dashboardsInCategories,
        dataFavorites,
        dataFlat,
        byoDashboards,
        error,
        isLoading,
        mutate
    };
}

export interface RecentDashboard {
    dashboard_name: string
}

export interface RecentDashboardsResponse {
    data: RecentDashboard[]
}

export function useRecentDashboards() {
    const {data, error, isLoading, mutate} = useSWR<RecentDashboardsResponse, Error>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/dashboards/recent`,
        null,
        {revalidateOnFocus: false});
    return {
        data,
        error,
        isLoading,
        mutate
    };
}

export function useDashboardGroups() {
    const {data} = useDashboardsInCategories();

    const groups = data ? Object.keys(data) : [];

    return {
        data: groups.filter(group => group !== "FAVORITE DASHBOARDS"),
    };
}

export function useDashboardByName(name: string) {
    const {dataFlat, error, mutate} = useDashboardsInCategories();
    return {
        data: dataFlat.find(dashboard => dashboard.name === name),
        error,
        mutate
    };
}
